import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { libraryService } from "../../service/question/libraryService";
import { useQuestionMeta } from "./QuestionMetaContext";
import {
  mapQuestionToLibraryItem,
  questionInitialQuery,
} from "../../pages/assessSections/helpers/questionLibraryHelpers";
import { questionEditorService } from "../../service/assessSections/questionEditorService";
import { questionService } from "../../service/assessment/questionService"; // adjust path if different
import { AzureStorage } from "../../helper/azureStorage";
import DataHandler from "../../handlers/DataHandler";
import { questionTypeConstants } from "../../helper/constants";


const QuestionLibraryContext = createContext(null);

const callListApi = async (apiFn, query) => {
  return apiFn(
    query.limit,
    query.offset,
    query.sort_column,
    query.sort_direction,
    query.search_term,
    query.category_id,
    query.selectedCategories,
    query.selectedAuthors,
    query.selectedChapters,
    query.selectedGrades,
    query.selectedDifficulties,
    query.selectedLanguages,
    query.selectedTypes,
    query.selectedTerms,
    query.selectedYears,
    query.selectedsubjects,
    query.selectedSources,
    query.selectedTeachers,
    query.selectedStartDate,
    query.selectedEndDate
  );
};

export function QuestionLibraryProvider({ children }) {
  const metaHelpers = useQuestionMeta();

  const [activeTab, setActiveTab] = useState("my");
  const [query, setQuery] = useState(questionInitialQuery);

  const [myQuestionsRaw, setMyQuestionsRaw] = useState([]);
  const [allQuestionsRaw, setAllQuestionsRaw] = useState([]);

  const [hasLoadedMyQuestions, setHasLoadedMyQuestions] = useState(false);
  const [hasLoadedAllQuestions, setHasLoadedAllQuestions] = useState(false);

  const [totalCount, setTotalCount] = useState(0);
  const [totalRealCount, setTotalRealCount] = useState(0);
  const [totalPublicCount, setTotalPublicCount] = useState(0);
  const [totalPublicRealCount, setTotalPublicRealCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const mountedRef = useRef(true);
  const loadedTabsRef = useRef({ my: false, all: false, });
  const loadingTabsRef = useRef({ my: false, all: false, });

  React.useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const myQuestions = useMemo(
    () => myQuestionsRaw.map((item) => mapQuestionToLibraryItem(item, metaHelpers)),
    [myQuestionsRaw, metaHelpers]
  );

  const allQuestions = useMemo(
    () => allQuestionsRaw.map((item) => mapQuestionToLibraryItem(item, metaHelpers)),
    [allQuestionsRaw, metaHelpers]
  );

  const activeItems = activeTab === "my" ? myQuestions : allQuestions;
  const fetchMyQuestions = useCallback(async (nextQuery, force = false) => {
    if (!force && loadedTabsRef.current.my) return;
    if (loadingTabsRef.current.my) return;

    loadingTabsRef.current.my = true;

    try {
      // console.log("fetchMyQuestions query:", nextQuery);

      const response = await callListApi(
        questionEditorService.getMyQuestionsLibraryPage,
        nextQuery
      );

      const data = response?.data || response || {};

      if (!mountedRef.current) return;

      setMyQuestionsRaw(data.myQuestions || data.questions || data.data || []);
      setTotalCount(data.total_count || data.totalCount || 0);
      setTotalRealCount(
        data.total_real_count || data.totalRealCount || data.total_count || 0
      );

      loadedTabsRef.current.my = true;
      setHasLoadedMyQuestions(true);
    } catch (err) {
      console.error("fetchMyQuestions failed:", err);
      throw err;
    } finally {
      loadingTabsRef.current.my = false;
    }
  }, []);

  const fetchAllQuestions = useCallback(async (nextQuery, force = false) => {
    if (!force && loadedTabsRef.current.all) return;
    if (loadingTabsRef.current.all) return;

    loadingTabsRef.current.all = true;

    try {
      // console.log("fetchAllQuestions query:", nextQuery);

      const response = await callListApi(
        questionEditorService.getAllQuestionsLibraryPage,
        nextQuery
      );

      const data = response?.data || response || {};

      if (!mountedRef.current) return;

      setAllQuestionsRaw(data.publicQuestions || data.questions || data.data || []);
      setTotalPublicCount(data.total_count || data.totalCount || 0);
      setTotalPublicRealCount(
        data.total_real_count || data.totalPublicRealCount || data.total_count || 0
      );

      loadedTabsRef.current.all = true;
      setHasLoadedAllQuestions(true);
    } catch (err) {
      console.error("fetchAllQuestions failed:", err);
      throw err;
    } finally {
      loadingTabsRef.current.all = false;
    }
  }, []);

  const fetchQuestions = useCallback(
    async (nextQuery = query, force = false) => {
      setLoading(true);
      setError("");

      try {
        if (activeTab === "my") {
          await fetchMyQuestions(nextQuery, force);
        } else {
          await fetchAllQuestions(nextQuery, force);
        }
      } catch (err) {
        console.error("fetchQuestions failed:", err);
        setError(err?.message || "Failed to load questions.");
      } finally {
        setLoading(false);
      }
    },
    [query, activeTab, fetchMyQuestions, fetchAllQuestions]
  );

  const applyQuery = useCallback(
    async (queryPatch) => {
      const nextQuery = {
        ...query,
        ...queryPatch,
        offset: queryPatch.offset ?? 0,
      };

      setQuery(nextQuery);
      loadedTabsRef.current = {
        my: false,
        all: false,
      };

      setHasLoadedMyQuestions(false);
      setHasLoadedAllQuestions(false);

      setLoading(true);
      setError("");

      try {
        if (activeTab === "my") {
          await fetchMyQuestions(nextQuery, true);
        } else {
          await fetchAllQuestions(nextQuery, true);
        }
      } catch (err) {
        console.error("applyQuery failed:", err);
        setError("Failed to load questions.");
      } finally {
        setLoading(false);
      }
    },
    [query, activeTab, fetchMyQuestions, fetchAllQuestions]
  );

  const changePage = useCallback(
    async (nextPage) => {
      const limit = Number(query.limit || 50);
      const nextOffset = (nextPage - 1) * limit;

      await applyQuery({
        offset: nextOffset,
      });
    },
    [query.limit, applyQuery]
  );

  const changePageSize = useCallback(
    async (nextLimit) => {
      await applyQuery({
        limit: Number(nextLimit),
        offset: 0,
      });
    },
    [applyQuery]
  );

  const currentPage = useMemo(() => {
    const limit = Number(query.limit || 50);
    const offset = Number(query.offset || 0);

    return Math.floor(offset / limit) + 1;
  }, [query.limit, query.offset]);

  const pageSize = useMemo(() => {
    return Number(query.limit || 50);
  }, [query.limit]);

  const deleteQuestionFromLibrary = useCallback(
    async (question) => {
      // 1. Block shared
      if (question.share) {
        throw new Error("Cannot remove a shared question.");
      }

      //  2. Check assessment usage
      const usageResponse = await questionEditorService.checkQuestionAssessmentUsage(
        question.question_id
      );

      if (usageResponse?.data?.is_used_in_assessment) {
        throw new Error("Can't remove questions added to assessments.");
      }

      //  3. Submission cleanup ONLY (keep this here)
      if (question.qtype === questionTypeConstants.SUBMISSION.id) {
        const userId = DataHandler.getFromSession("user_id");
        const containerName = `${userId}-a-${question.answer}`;

        await AzureStorage.removeBlobContent(containerName, question.answer);
      }

      //  4. Backend delete (service handles rest)
      const response = await libraryService.deleteQuestion(question);

      if (!response?.success) {
        throw new Error("Question delete failed.");
      }

      const deletedQuestionId = question.question_id;

      const existsInMyQuestions = myQuestionsRaw.some(
        (item) => item.question_id === deletedQuestionId
      );

      const existsInAllQuestions = allQuestionsRaw.some(
        (item) => item.question_id === deletedQuestionId
      );

      setMyQuestionsRaw((prev) =>
        prev.filter((item) => item.question_id !== deletedQuestionId)
      );

      setAllQuestionsRaw((prev) =>
        prev.filter((item) => item.question_id !== deletedQuestionId)
      );

      if (existsInMyQuestions) {
        setTotalRealCount((count) => Math.max(0, count - 1));
      }

      if (existsInAllQuestions) {
        setTotalPublicRealCount((count) => Math.max(0, count - 1));
      }

      return { success: true };
    },
    [myQuestionsRaw, allQuestionsRaw]
  );


  //in case both my and all questions tab needs to be refreshed 
  const refreshBothTabs = useCallback(async (force = true) => {
    setLoading(true);
    setError("");

    try {
      await Promise.all([
        fetchMyQuestions(query, force),
        fetchAllQuestions(query, force),
      ]);
    } catch (err) {
      setError("Failed to refresh questions.");
    } finally {
      setLoading(false);
    }
  }, [query, fetchMyQuestions, fetchAllQuestions]);



  const changeTab = useCallback(
    async (nextTab) => {
      setActiveTab(nextTab);

      if (nextTab === "my" && !hasLoadedMyQuestions) {
        await fetchMyQuestions(query);
      }

      if (nextTab === "all" && !hasLoadedAllQuestions) {
        await fetchAllQuestions(query);
      }
    },
    [
      query,
      hasLoadedMyQuestions,
      hasLoadedAllQuestions,
      fetchMyQuestions,
      fetchAllQuestions,
    ]
  );

  const value = useMemo(
    () => ({
      activeTab,
      setActiveTab: changeTab,

      query,
      applyQuery,
      changePage,

      myQuestions,
      allQuestions,
      activeItems,

      totalCount,
      totalRealCount,
      totalPublicCount,
      totalPublicRealCount,

      loading,
      error,

      fetchQuestions,
      fetchMyQuestions,
      fetchAllQuestions,
      refreshBothTabs,
      // createdByOptions,
      deleteQuestionFromLibrary,

      changePageSize,
      currentPage,
      pageSize,

    }),
    [
      activeTab,
      changeTab,
      query,
      applyQuery,
      changePage,
      myQuestions,
      allQuestions,
      activeItems,
      totalCount,
      totalRealCount,
      totalPublicCount,
      totalPublicRealCount,
      loading,
      error,
      fetchQuestions,
      fetchMyQuestions,
      fetchAllQuestions,
      refreshBothTabs,
      // createdByOptions,
      deleteQuestionFromLibrary,
      changePageSize,
      currentPage,
      pageSize,
    ]
  );

  return (
    <QuestionLibraryContext.Provider value={value}>
      {children}
    </QuestionLibraryContext.Provider>
  );
}

export function useQuestionLibrary() {
  const context = useContext(QuestionLibraryContext);

  if (!context) {
    throw new Error(
      "useQuestionLibrary must be used inside QuestionLibraryProvider"
    );
  }

  return context;
}